import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Calendar, FileText, AlertTriangle, CheckCircle, 
  XCircle, Clock, Bell, Download, ChevronRight, Lock,
  Activity, Clipboard, AlertCircle, TrendingUp, TrendingDown,
  Mail, MessageSquare, Settings, Filter, Search, RefreshCw,
  FileCheck, Briefcase, Heart, Pill, Truck, ExternalLink
} from 'lucide-react';

// ============ PROTECT COMMAND CENTER ============
// Compliance Monitoring Command Center with full operational dashboard

// Subscription tier features - ALIGNED WITH PRICING PAGE
const PROTECT_TIERS = {
  none: { 
    name: 'Not Subscribed', 
    features: [],
    tabs: [],
    monitoredItems: [],
    monitoredCategories: []
  },
  basic: { 
    name: 'Basic Monitoring', 
    price: 197,
    features: ['compliance_overview', 'document_status', 'basic_alerts', 'snapshot_pdf'],
    // BASIC: Insurance expiration, Annual UCR, Monthly status report, Email alerts, Basic guidance
    tabs: ['overview', 'documents', 'subscription'],
    // Only insurance and UCR for basic
    monitoredItems: ['Insurance - Liability', 'Insurance - Cargo', 'UCR Registration'],
    monitoredCategories: [], // Don't use category matching for basic - be explicit
    alerts: ['email'],
    reports: ['snapshot_pdf']
  },
  professional: { 
    name: 'Professional Monitoring', 
    price: 297,
    features: ['compliance_overview', 'document_status', 'compliance_calendar', 'audit_readiness', 'violation_tracking', 'sms_alerts', 'email_alerts', 'snapshot_pdf', 'deadline_calendar_pdf'],
    // PRO: Everything in Basic + IFTA/IRP, Compliance calendar, Bi-weekly updates, Priority support, Safety score, Audit prep
    tabs: ['overview', 'calendar', 'documents', 'audit', 'violations', 'reports', 'subscription'],
    monitoredItems: ['Insurance - Liability', 'Insurance - Cargo', 'UCR Registration', 'IFTA License', 'IRP Registration', 'BOC-3 Filing'],
    monitoredCategories: [], // Explicit items only
    alerts: ['email', 'sms'],
    reports: ['snapshot_pdf', 'deadline_calendar_pdf']
  },
  enterprise: { 
    name: 'Enterprise Monitoring', 
    price: 397,
    features: ['compliance_overview', 'document_status', 'compliance_calendar', 'audit_readiness', 'violation_tracking', 'sms_alerts', 'email_alerts', 'clearinghouse', 'full_packet_zip', 'health_history', 'risk_analysis', 'priority_support'],
    // ENTERPRISE: Everything in Professional + Dedicated manager, Weekly calls, Real-time alerts, Custom dashboard, Multi-unit, Direct liaison
    tabs: ['overview', 'calendar', 'documents', 'audit', 'violations', 'reports', 'subscription'],
    // Enterprise gets EVERYTHING
    monitoredItems: ['DOT Authority', 'MC Authority', 'Insurance - Liability', 'Insurance - Cargo', 'BOC-3 Filing', 'UCR Registration', 'IFTA License', 'IRP Registration', 'Drug & Alcohol Program', 'Clearinghouse Query', 'Medical Card'],
    monitoredCategories: ['authority', 'insurance', 'registration', 'filing', 'safety', 'driver'],
    alerts: ['email', 'sms', 'realtime'],
    reports: ['snapshot_pdf', 'deadline_calendar_pdf', 'full_packet_zip', 'health_history']
  }
};

// Internal tabs for Protect - Will be filtered by tier
const ALL_PROTECT_TABS = [
  { id: 'overview', label: 'Compliance Overview', icon: Activity },
  { id: 'calendar', label: 'Compliance Calendar', icon: Calendar },
  { id: 'documents', label: 'Document Status', icon: FileText },
  { id: 'audit', label: 'Audit Readiness', icon: Clipboard },
  { id: 'violations', label: 'Violation Tracking', icon: AlertTriangle },
  { id: 'reports', label: 'Reports & Export', icon: Download },
  { id: 'subscription', label: 'Manage Subscription', icon: Settings }
];

// Mock compliance data
const MOCK_COMPLIANCE_DATA = {
  health_score: 92,
  upcoming_deadlines: 3,
  missing_documents: 1,
  violations: 0,
  drug_alcohol_status: 'compliant',
  items: [
    { name: 'DOT Authority', status: 'valid', expires: null, category: 'authority' },
    { name: 'MC Authority', status: 'valid', expires: null, category: 'authority' },
    { name: 'Insurance - Liability', status: 'valid', expires: '2026-06-30', category: 'insurance' },
    { name: 'Insurance - Cargo', status: 'valid', expires: '2026-06-30', category: 'insurance' },
    { name: 'BOC-3 Filing', status: 'valid', expires: null, category: 'filing' },
    { name: 'UCR Registration', status: 'expiring', expires: '2026-03-15', category: 'registration' },
    { name: 'IFTA License', status: 'valid', expires: '2026-12-31', category: 'registration' },
    { name: 'IRP Registration', status: 'valid', expires: '2026-12-31', category: 'registration' },
    { name: 'Drug & Alcohol Program', status: 'compliant', expires: null, category: 'safety' },
    { name: 'Clearinghouse Query', status: 'due_soon', expires: '2026-03-01', category: 'safety' },
    { name: 'Medical Card', status: 'valid', expires: '2026-08-15', category: 'driver' }
  ],
  calendar_events: [
    { date: '2026-03-01', title: 'Clearinghouse Annual Query Due', type: 'deadline', priority: 'high' },
    { date: '2026-03-15', title: 'UCR Registration Renewal', type: 'deadline', priority: 'high' },
    { date: '2026-03-31', title: 'IFTA Q1 Filing Due', type: 'deadline', priority: 'medium' },
    { date: '2026-06-30', title: 'Insurance Renewal', type: 'deadline', priority: 'medium' },
    { date: '2026-08-15', title: 'Medical Card Expiration', type: 'deadline', priority: 'low' }
  ],
  audit_checklist: [
    { item: 'Driver Qualification Files', status: 'complete', required: true },
    { item: 'Vehicle Maintenance Records', status: 'complete', required: true },
    { item: 'Hours of Service Logs', status: 'complete', required: true },
    { item: 'Drug & Alcohol Testing Records', status: 'complete', required: true },
    { item: 'Accident Register', status: 'incomplete', required: true },
    { item: 'Insurance Certificates', status: 'complete', required: true },
    { item: 'Annual Vehicle Inspections', status: 'complete', required: true },
    { item: 'Roadside Inspection Reports', status: 'complete', required: false }
  ]
};

// Stat Card Component - Now with drill-down navigation
const StatCard = ({ label, value, icon: Icon, color, trend, onClick, locked, drillDownKey }) => (
  <div 
    className={`bg-[#121212] border border-white/10 p-4 sm:p-6 ${onClick && !locked ? 'cursor-pointer hover:border-[#C0C0C0]/30 hover:bg-[#C0C0C0]/5 transition-all' : ''} ${locked ? 'opacity-60' : ''}`}
    onClick={!locked ? onClick : undefined}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-white/50 text-xs sm:text-sm uppercase">{label}</span>
      {locked ? (
        <Lock className="w-4 h-4 text-white/30" />
      ) : drillDownKey ? (
        <ExternalLink className="w-4 h-4 text-[#C0C0C0] opacity-0 group-hover:opacity-100 transition-opacity" />
      ) : (
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
      )}
    </div>
    <div className="flex items-end justify-between">
      <div className="flex items-end gap-2">
        <p className="text-xl sm:text-2xl font-bold text-white">{locked ? '—' : value}</p>
        {trend && !locked && (
          <span className={`text-xs flex items-center gap-1 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      {onClick && !locked && (
        <ChevronRight className="w-4 h-4 text-white/30" />
      )}
    </div>
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    valid: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Valid' },
    compliant: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Compliant' },
    complete: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Complete' },
    expiring: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Expiring Soon' },
    due_soon: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Due Soon' },
    incomplete: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Incomplete' },
    expired: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Expired' },
    violation: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Violation' },
    missing: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Missing' }
  };
  const c = config[status] || config.valid;
  return (
    <span className={`px-2 py-1 text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Compliance Overview Tab - Filtered by tier
const ComplianceOverview = ({ data, onDrillDown, subscriptionTier }) => {
  const tierConfig = PROTECT_TIERS[subscriptionTier] || PROTECT_TIERS.basic;
  const monitoredItems = tierConfig.monitoredItems || [];
  const monitoredCategories = tierConfig.monitoredCategories || [];
  
  // Filter items based on tier
  const filteredItems = data.items.filter(item => 
    monitoredItems.includes(item.name) || monitoredCategories.includes(item.category)
  );
  
  // Calculate health score based only on monitored items
  const validCount = filteredItems.filter(i => i.status === 'valid' || i.status === 'compliant').length;
  const tierHealthScore = filteredItems.length > 0 ? Math.round((validCount / filteredItems.length) * 100) : 100;
  
  return (
    <div className="space-y-6" data-testid="compliance-overview">
      {/* Tier Badge */}
      <div className="flex items-center gap-3 mb-2">
        <span className="px-3 py-1 text-xs font-medium uppercase tracking-wider bg-[#C0C0C0]/20 text-[#C0C0C0] border border-[#C0C0C0]/30">
          {tierConfig.name}
        </span>
        <span className="text-white/40 text-sm">
          Monitoring {filteredItems.length} compliance items
        </span>
      </div>
      
      {/* Health Score Card */}
      <div className="bg-gradient-to-r from-[#121212] to-[#1a1a1a] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold uppercase tracking-wider">Compliance Health Score</h3>
          <span className="text-white/50 text-sm">Based on your {tierConfig.name} plan</span>
        </div>
        <div className="flex items-center gap-6">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="64" cy="64" r="56" fill="none" stroke="#1f1f1f" strokeWidth="12" />
              <circle 
                cx="64" cy="64" r="56" fill="none" 
                stroke={tierHealthScore >= 80 ? '#10B981' : tierHealthScore >= 60 ? '#F59E0B' : '#EF4444'}
                strokeWidth="12" 
                strokeDasharray={`${tierHealthScore * 3.52} 352`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{tierHealthScore}%</span>
            </div>
          </div>
          <div className="flex-1 space-y-2">
            {subscriptionTier === 'enterprise' && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">Authority Status</span>
                <StatusBadge status="valid" />
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Insurance Status</span>
              <StatusBadge status="valid" />
            </div>
            {subscriptionTier !== 'basic' && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">Safety Rating</span>
                <StatusBadge status="compliant" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compliance Items Grid - FILTERED BY TIER */}
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold uppercase tracking-wider">Items Being Monitored</h3>
          <div className="flex items-center gap-2">
            <span className="text-green-400 text-xs">✓ {filteredItems.filter(i => i.status === 'valid' || i.status === 'compliant').length} Valid</span>
            <span className="text-amber-400 text-xs">⚠ {filteredItems.filter(i => i.status === 'expiring' || i.status === 'due_soon').length} Attention</span>
          </div>
        </div>
        <div className="divide-y divide-white/5">
          {filteredItems.length === 0 ? (
            <div className="p-8 text-center text-white/50">
              <Shield className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p>No compliance items being monitored</p>
              <p className="text-sm mt-1">Upgrade your plan for more coverage</p>
            </div>
          ) : (
            filteredItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 hover:bg-white/5">
                <div className="flex items-center gap-3">
                  {item.status === 'valid' || item.status === 'compliant' ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : item.status === 'expiring' || item.status === 'due_soon' ? (
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <div>
                    <p className="text-white font-medium">{item.name}</p>
                    {item.expires && (
                      <p className="text-white/50 text-xs">Expires: {item.expires}</p>
                    )}
                  </div>
                </div>
                <StatusBadge status={item.status} />
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Upgrade Prompt for Basic tier */}
      {subscriptionTier === 'basic' && (
        <div className="bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#C0C0C0]" />
            <div>
              <p className="text-white font-medium">Want more comprehensive monitoring?</p>
              <p className="text-white/60 text-sm">Upgrade to Professional for IFTA/IRP tracking, compliance calendar, and audit preparation.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Compliance Calendar Tab
const ComplianceCalendar = ({ data }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  
  return (
    <div className="space-y-6" data-testid="compliance-calendar">
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Upcoming Deadlines</h3>
        <div className="space-y-3">
          {data.calendar_events.map((event, idx) => (
            <div key={idx} className={`flex items-center justify-between p-4 border-l-4 ${
              event.priority === 'high' ? 'border-red-500 bg-red-500/5' :
              event.priority === 'medium' ? 'border-amber-500 bg-amber-500/5' :
              'border-blue-500 bg-blue-500/5'
            }`}>
              <div className="flex items-center gap-4">
                <Calendar className={`w-5 h-5 ${
                  event.priority === 'high' ? 'text-red-400' :
                  event.priority === 'medium' ? 'text-amber-400' : 'text-blue-400'
                }`} />
                <div>
                  <p className="text-white font-medium">{event.title}</p>
                  <p className="text-white/50 text-sm">{event.date}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium uppercase ${
                event.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                event.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {event.priority}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid Placeholder */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Calendar View</h3>
        <div className="grid grid-cols-7 gap-1 text-center text-white/50 text-sm">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 font-medium">{day}</div>
          ))}
          {Array.from({ length: 35 }, (_, i) => (
            <div key={i} className="p-2 bg-[#0A0A0A] hover:bg-white/5 cursor-pointer min-h-[60px]">
              <span className="text-white/30">{(i % 31) + 1}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Document Status Tab - Filtered by tier
const DocumentStatus = ({ data, subscriptionTier }) => {
  const tierConfig = PROTECT_TIERS[subscriptionTier] || PROTECT_TIERS.basic;
  
  // Documents available by tier
  const allDocs = [
    { name: 'Certificate of Insurance', status: 'valid', uploaded: '2025-12-01', tiers: ['basic', 'professional', 'enterprise'] },
    { name: 'BOC-3 Filing', status: 'valid', uploaded: '2025-10-15', tiers: ['professional', 'enterprise'] },
    { name: 'Authority Letter', status: 'valid', uploaded: '2025-09-01', tiers: ['enterprise'] },
    { name: 'W-9 Form', status: 'valid', uploaded: '2025-11-20', tiers: ['basic', 'professional', 'enterprise'] },
    { name: 'Drug Testing Enrollment', status: 'valid', uploaded: '2025-10-01', tiers: ['enterprise'] },
    { name: 'UCR Receipt', status: 'expiring', uploaded: '2025-01-15', tiers: ['basic', 'professional', 'enterprise'] },
    { name: 'IFTA License', status: 'valid', uploaded: '2025-10-01', tiers: ['professional', 'enterprise'] },
    { name: 'IRP Registration', status: 'valid', uploaded: '2025-10-01', tiers: ['professional', 'enterprise'] }
  ];
  
  const filteredDocs = allDocs.filter(doc => doc.tiers.includes(subscriptionTier));
  
  return (
    <div className="space-y-6" data-testid="document-status">
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold uppercase tracking-wider">Document Vault Status</h3>
          <span className="text-white/50 text-sm">{filteredDocs.length} documents tracked</span>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map((doc, idx) => (
            <div key={idx} className="bg-[#0A0A0A] border border-white/5 p-4">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-5 h-5 text-[#C0C0C0]" />
                <StatusBadge status={doc.status} />
              </div>
              <p className="text-white font-medium text-sm mb-1">{doc.name}</p>
              <p className="text-white/40 text-xs">Uploaded: {doc.uploaded}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Upgrade prompt for Basic users */}
      {subscriptionTier === 'basic' && (
        <div className="bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 p-4">
          <p className="text-white/70 text-sm">
            <span className="text-[#C0C0C0] font-medium">Upgrade to Professional</span> to track IFTA, IRP, and BOC-3 documents
          </p>
        </div>
      )}
    </div>
  );
};

// Audit Readiness Tab
const AuditReadiness = ({ data }) => {
  const completedCount = data.audit_checklist.filter(i => i.status === 'complete').length;
  const totalRequired = data.audit_checklist.filter(i => i.required).length;
  const readinessScore = Math.round((completedCount / data.audit_checklist.length) * 100);
  
  return (
    <div className="space-y-6" data-testid="audit-readiness">
      {/* Readiness Score */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold uppercase tracking-wider">Audit Readiness Score</h3>
          <span className={`text-2xl font-bold ${
            readinessScore >= 90 ? 'text-green-400' : readinessScore >= 70 ? 'text-amber-400' : 'text-red-400'
          }`}>
            {readinessScore}%
          </span>
        </div>
        <div className="w-full h-3 bg-[#0A0A0A] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${
              readinessScore >= 90 ? 'bg-green-500' : readinessScore >= 70 ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${readinessScore}%` }}
          />
        </div>
        <p className="text-white/50 text-sm mt-2">
          {completedCount} of {data.audit_checklist.length} items complete • {totalRequired - data.audit_checklist.filter(i => i.required && i.status === 'complete').length} required items remaining
        </p>
      </div>

      {/* Checklist */}
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-bold uppercase tracking-wider">Audit Checklist</h3>
        </div>
        <div className="divide-y divide-white/5">
          {data.audit_checklist.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 hover:bg-white/5">
              <div className="flex items-center gap-3">
                {item.status === 'complete' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-amber-400" />
                )}
                <div>
                  <p className="text-white font-medium">{item.item}</p>
                  {item.required && <span className="text-red-400 text-xs">Required</span>}
                </div>
              </div>
              <StatusBadge status={item.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Violation Tracking Tab
const ViolationTracking = ({ data }) => (
  <div className="space-y-6" data-testid="violation-tracking">
    <div className="bg-[#121212] border border-white/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold uppercase tracking-wider">Violation Summary</h3>
        <span className="text-green-400 font-bold">0 Active Violations</span>
      </div>
      <div className="text-center py-8 text-white/50">
        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
        <p className="text-lg">No violations on record</p>
        <p className="text-sm mt-2">Your compliance record is clean</p>
      </div>
    </div>

    {/* BASIC Scores Placeholder */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">FMCSA BASIC Scores</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { name: 'Unsafe Driving', score: 12, threshold: 65 },
          { name: 'HOS Compliance', score: 8, threshold: 65 },
          { name: 'Driver Fitness', score: 0, threshold: 80 },
          { name: 'Controlled Substances', score: 0, threshold: 80 },
          { name: 'Vehicle Maintenance', score: 15, threshold: 80 },
          { name: 'Crash Indicator', score: 0, threshold: 65 }
        ].map((basic, idx) => (
          <div key={idx} className="bg-[#0A0A0A] p-4">
            <p className="text-white/50 text-xs uppercase mb-2">{basic.name}</p>
            <p className={`text-2xl font-bold ${basic.score < basic.threshold * 0.5 ? 'text-green-400' : basic.score < basic.threshold ? 'text-amber-400' : 'text-red-400'}`}>
              {basic.score}%
            </p>
            <p className="text-white/30 text-xs">Threshold: {basic.threshold}%</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Reports Tab
const ReportsExport = ({ subscriptionTier }) => {
  const tierFeatures = PROTECT_TIERS[subscriptionTier]?.features || [];
  
  return (
    <div className="space-y-6" data-testid="reports-export">
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Audit & Compliance Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { id: 'snapshot_pdf', name: 'Compliance Snapshot', desc: 'One-page executive summary', icon: FileText, tier: 'basic' },
            { id: 'deadline_calendar_pdf', name: 'Deadline Calendar Export', desc: 'Upcoming deadlines PDF', icon: Calendar, tier: 'professional' },
            { id: 'full_packet_zip', name: 'Full Compliance Packet', desc: 'All documents in ZIP', icon: Download, tier: 'enterprise' },
            { id: 'health_history', name: 'Health History Report', desc: 'Score trends over time', icon: Activity, tier: 'enterprise' }
          ].map((report) => {
            const isLocked = !tierFeatures.includes(report.id);
            return (
              <div key={report.id} className={`bg-[#0A0A0A] border border-white/5 p-4 ${isLocked ? 'opacity-60' : 'hover:border-[#C0C0C0]/30 cursor-pointer'}`}>
                <div className="flex items-center justify-between mb-2">
                  <report.icon className="w-6 h-6 text-[#C0C0C0]" />
                  {isLocked && <Lock className="w-4 h-4 text-white/30" />}
                </div>
                <p className="text-white font-medium">{report.name}</p>
                <p className="text-white/50 text-sm mb-3">{report.desc}</p>
                <button 
                  className={`w-full py-2 text-sm font-medium ${isLocked ? 'bg-white/5 text-white/30' : 'bg-[#C0C0C0] text-black hover:bg-[#A0A0A0]'}`}
                  disabled={isLocked}
                >
                  {isLocked ? `Requires ${report.tier.charAt(0).toUpperCase() + report.tier.slice(1)}` : 'Generate Report'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Subscription Management Tab
const SubscriptionManagement = ({ currentTier, onUpgrade }) => (
  <div className="space-y-6" data-testid="subscription-management">
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Current Plan</h3>
      <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#C0C0C0]/30">
        <div>
          <p className="text-[#C0C0C0] font-bold text-lg">{PROTECT_TIERS[currentTier]?.name || 'Not Subscribed'}</p>
          {PROTECT_TIERS[currentTier]?.price && (
            <p className="text-white/50">${PROTECT_TIERS[currentTier].price}/month</p>
          )}
        </div>
        <StatusBadge status={currentTier !== 'none' ? 'valid' : 'missing'} />
      </div>
    </div>

    {currentTier === 'none' && (
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Available Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(PROTECT_TIERS).filter(([k]) => k !== 'none').map(([key, tier]) => (
            <div key={key} className="bg-[#0A0A0A] border border-white/10 p-4 hover:border-[#C0C0C0]/30">
              <p className="text-[#C0C0C0] font-bold mb-1">{tier.name}</p>
              <p className="text-2xl font-bold text-white mb-4">${tier.price}<span className="text-sm text-white/50">/mo</span></p>
              <button 
                onClick={() => onUpgrade(key)}
                className="w-full py-2 bg-[#C0C0C0] text-black font-medium hover:bg-[#A0A0A0]"
              >
                Activate Monitoring
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Preview Overlay Component
const PreviewOverlay = ({ onActivate }) => (
  <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-10" data-testid="protect-preview-overlay">
    <div className="text-center max-w-md p-8">
      <Shield className="w-16 h-16 text-[#C0C0C0] mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        PROTECT COMMAND CENTER
      </h2>
      <p className="text-white/60 mb-6">
        Comprehensive compliance monitoring to keep your authority protected and your business running smoothly.
      </p>
      <button 
        onClick={onActivate}
        className="px-8 py-3 bg-[#C0C0C0] text-black font-bold uppercase tracking-wider hover:bg-[#A0A0A0]"
      >
        Activate Monitoring
      </button>
    </div>
  </div>
);

// ============ MAIN PROTECT COMMAND CENTER COMPONENT ============
const ProtectCommandCenter = ({ 
  subscriptionStatus = 'none', 
  subscriptionTier = 'none',
  tierColor = '#C0C0C0',
  onActivate 
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(MOCK_COMPLIANCE_DATA);
  const isSubscribed = subscriptionStatus === 'active' || subscriptionStatus === 'selected' || subscriptionTier !== 'none';
  
  // Get tier configuration
  const tierConfig = PROTECT_TIERS[subscriptionTier] || PROTECT_TIERS.none;
  const availableTabs = tierConfig.tabs || [];
  
  // Filter tabs based on tier
  const PROTECT_TABS = ALL_PROTECT_TABS.filter(tab => 
    availableTabs.includes(tab.id)
  );

  // Handler for drill-down navigation
  const handleDrillDown = (metricKey) => {
    navigate(`/hive/drill-down/${metricKey}`);
  };

  // Calculate tier-specific stats
  const monitoredItems = tierConfig.monitoredItems || [];
  const monitoredCategories = tierConfig.monitoredCategories || [];
  const filteredItems = data.items.filter(item => 
    monitoredItems.includes(item.name) || monitoredCategories.includes(item.category)
  );
  const validCount = filteredItems.filter(i => i.status === 'valid' || i.status === 'compliant').length;
  const tierHealthScore = filteredItems.length > 0 ? Math.round((validCount / filteredItems.length) * 100) : 100;
  const upcomingDeadlines = filteredItems.filter(i => i.status === 'expiring' || i.status === 'due_soon').length;
  const missingDocs = filteredItems.filter(i => i.status === 'missing' || i.status === 'expired').length;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <ComplianceOverview data={data} onDrillDown={handleDrillDown} subscriptionTier={subscriptionTier} />;
      case 'calendar':
        return <ComplianceCalendar data={data} />;
      case 'documents':
        return <DocumentStatus data={data} subscriptionTier={subscriptionTier} />;
      case 'audit':
        return <AuditReadiness data={data} />;
      case 'violations':
        return <ViolationTracking data={data} />;
      case 'reports':
        return <ReportsExport subscriptionTier={subscriptionTier} />;
      case 'subscription':
        return <SubscriptionManagement currentTier={subscriptionTier} onUpgrade={onActivate} />;
      default:
        return <ComplianceOverview data={data} onDrillDown={handleDrillDown} subscriptionTier={subscriptionTier} />;
    }
  };

  return (
    <div className="relative" data-testid="protect-command-center">
      {/* Preview Overlay for non-subscribers */}
      {!isSubscribed && <PreviewOverlay onActivate={onActivate} />}
      
      {/* Header with Tier Badge */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            PROTECT
          </h1>
          {isSubscribed && (
            <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#C0C0C0]/20 text-[#C0C0C0] border border-[#C0C0C0]/30">
              {tierConfig.name}
            </span>
          )}
        </div>
        <p className="text-[#C0C0C0]">Compliance Monitoring Command Center</p>
      </div>

      {/* Top Metric Cards - Filtered by tier */}
      <div className={`grid grid-cols-2 ${subscriptionTier === 'basic' ? 'md:grid-cols-3' : 'md:grid-cols-5'} gap-3 mb-6`}>
        <StatCard 
          label="Compliance Score" 
          value={`${tierHealthScore}%`} 
          icon={Shield} 
          color="#10B981"
          onClick={() => handleDrillDown('compliance-score')}
          drillDownKey="compliance-score"
        />
        <StatCard 
          label="Items Monitored" 
          value={filteredItems.length} 
          icon={FileText} 
          color="#C0C0C0"
        />
        <StatCard 
          label="Attention Needed" 
          value={upcomingDeadlines} 
          icon={Calendar} 
          color={upcomingDeadlines > 0 ? '#F59E0B' : '#10B981'}
          onClick={() => handleDrillDown('upcoming-deadlines')}
          drillDownKey="upcoming-deadlines"
        />
        {/* Professional+ features */}
        {subscriptionTier !== 'basic' && (
          <>
            <StatCard 
              label="Violations" 
              value={data.violations} 
              icon={AlertTriangle} 
              color={data.violations > 0 ? '#EF4444' : '#10B981'}
              onClick={() => handleDrillDown('violations')}
              drillDownKey="violations"
            />
            <StatCard 
              label="D&A Status" 
              value={data.drug_alcohol_status === 'compliant' ? '✓' : '!'} 
              icon={Heart} 
              color={data.drug_alcohol_status === 'compliant' ? '#10B981' : '#EF4444'}
              onClick={() => handleDrillDown('drug-alcohol-status')}
              drillDownKey="drug-alcohol-status"
              locked={subscriptionTier !== 'enterprise'}
            />
          </>
        )}
      </div>

      {/* Internal Tabs - Filtered by tier */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
        {PROTECT_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-[#C0C0C0] text-black' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`protect-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className={`${!isSubscribed ? 'filter blur-sm pointer-events-none' : ''}`}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ProtectCommandCenter;
